import React, { useState, useEffect } from 'react';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { TeamMember } from '../types';
import { apiService } from '../services/api';

const Team: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const team = await apiService.getTeam();
        setTeamMembers(team);
      } catch (err) {
        console.error('Error fetching team members:', err);
        setError('Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-church-light py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-church-light py-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-church-blue text-white px-4 py-2 rounded hover:bg-blue-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Meet Our Parish Team
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our dedicated team is here to serve you and help you grow in faith. 
            Don't hesitate to reach out to any of us.
          </p>
        </header>

        {teamMembers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No team members found.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <article
                  key={member.id}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
                >
                  <div className="p-6">
                    {/* Photo placeholder */}
                    <div className="w-32 h-32 mx-auto mb-4 bg-church-blue rounded-full flex items-center justify-center text-white text-4xl font-bold">
                      {member.photoUrl ? (
                        <img
                          src={member.photoUrl}
                          alt={`${member.name}, ${member.role}`}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        member.name.split(' ').map(n => n[0]).join('')
                      )}
                    </div>

                    <div className="text-center">
                      <h3 className="text-xl font-semibold text-church-dark mb-1">
                        {member.name}
                      </h3>
                      <p className="text-church-gold font-medium mb-4">
                        {member.role}
                      </p>
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed mb-6">
                      {member.bio}
                    </p>

                    <div className="space-y-2 pt-4 border-t border-gray-200">
                      {member.email && (
                        <a
                          href={`mailto:${member.email}`}
                          className="flex items-center text-church-blue hover:text-blue-800 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-1"
                        >
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          {member.email}
                        </a>
                      )}
                      {member.phone && (
                        <a
                          href={`tel:${member.phone.replace(/[^\d]/g, '')}`}
                          className="flex items-center text-church-blue hover:text-blue-800 text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue rounded px-1"
                        >
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          {member.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Team;